import React from 'react'
import { Button, Icon } from '@popety-io/ui'
import { Transaction } from '@popety-io/types'
import { TransactionBadge, PropertyTypeBadge } from './Transactions.styles'
import styled from 'styled-components'

const DetailContainer = styled.div`
  margin-bottom: 48px;
`

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 500;
  text-decoration: none;
  margin-bottom: 24px;
  transition: all 0.2s;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    gap: 8px;
  }
`

const Card = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  margin-bottom: 24px;
  overflow: hidden;
`

const CardHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`

const CardTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 12px;
`

const CardBody = styled.div`
  padding: 24px;
`

const DetailLocation = styled.div`
  margin-bottom: 4px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: 6px;
`

const DetailDate = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin-bottom: 8px;
`

const WarningBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  margin-bottom: 24px;
  padding: 8px 12px;
  background: rgba(255, 149, 0, 0.1);
  border-radius: 6px;
  font-size: 13px;
  color: #ff9500;
`

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`

const DataGroup = styled.div``

const DataSectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 16px;
`

const DataRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  gap: 12px;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`

const DataLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

const DataValue = styled.span<{ $primary?: boolean }>`
  font-size: 13px;
  font-weight: ${({ $primary }) => ($primary ? 600 : 500)};
  color: ${({ theme, $primary }) =>
    $primary ? theme.colors.primary : theme.colors.text.primary};
  text-align: right;
  word-break: break-word;
`

const DescriptionBox = styled.div`
  margin-top: 32px;
`

const Description = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => theme.colors.background.tertiary};
  padding: 16px;
  border-radius: 6px;
`

const PropertyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`

const PropertyItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: 6px;
  font-size: 13px;
`

const PropertyNumber = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`

const PropertyDescription = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
`

const PropertyArea = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
`

interface TransactionDetailProps {
  transaction: Transaction
  onBack: () => void
}

export const TransactionDetail: React.FC<TransactionDetailProps> = ({
  transaction,
  onBack,
}) => {
  const formatPrice = (price: number) => {
    return `CHF ${price.toLocaleString('fr-CH')}`
  }

  const formatPricePerM2 = (pricePerM2: number) => {
    return `CHF ${pricePerM2.toLocaleString('fr-CH')} /m²`
  }

  const formatArea = (area: number) => {
    return `${area} m²`
  }

  const getTransactionBadgeText = (reason: string) => {
    const badgeMap: Record<string, string> = {
      achat: 'Achat',
      heritage: 'Héritage',
      vente: 'Vente',
      donation: 'Donation',
      exchange: 'Exchange',
    }
    return badgeMap[reason] || reason
  }

  const getPropertyTypeText = (type: string) => {
    const typeMap: Record<string, string> = {
      ppe: 'PPE',
      'bien-fonds': 'Bien-fonds (B-F)',
      individuelle: 'Individuelle',
      commune: 'Communauté héréditaire',
    }
    return typeMap[type] || type
  }

  return (
    <DetailContainer>
      {/* Back Button */}
      <BackButton onClick={onBack}>
        <Icon name="arrow-left" size="small" />
        Back to the list
      </BackButton>

      {/* Single Detail Card with All Information */}
      <Card>
        <CardHeader>
          <CardTitle>
            Transaction {transaction.id}
            <TransactionBadge $variant={transaction.badge}>
              {getTransactionBadgeText(transaction.reason)}
            </TransactionBadge>
            <PropertyTypeBadge>
              {getPropertyTypeText(transaction.type)}
            </PropertyTypeBadge>
          </CardTitle>
        </CardHeader>

        <CardBody>
          <div style={{ marginBottom: '24px' }}>
            <DetailLocation>
              <Icon name="location" size="small" />
              <span>{transaction.address || transaction.location}</span>
            </DetailLocation>
            <DetailDate>{transaction.date}</DetailDate>

            {transaction.multiple_plots && (
              <WarningBox>
                <Icon name="alert-triangle" size="small" />
                This transaction involves several plots of land
              </WarningBox>
            )}
          </div>

          <DataGrid>
            {/* Left Column */}
            <DataGroup>
              <DataSectionTitle>Transaction Information</DataSectionTitle>

              <DataRow>
                <DataLabel>Reason</DataLabel>
                <DataValue>
                  {getTransactionBadgeText(transaction.reason)}
                </DataValue>
              </DataRow>

              <DataRow>
                <DataLabel>Type</DataLabel>
                <DataValue>{getPropertyTypeText(transaction.type)}</DataValue>
              </DataRow>

              {transaction.price && (
                <DataRow>
                  <DataLabel>Price</DataLabel>
                  <DataValue $primary>
                    {formatPrice(transaction.price)}
                  </DataValue>
                </DataRow>
              )}

              {transaction.price_per_m2 && (
                <DataRow>
                  <DataLabel>Price per m²</DataLabel>
                  <DataValue>
                    {formatPricePerM2(transaction.price_per_m2)}
                  </DataValue>
                </DataRow>
              )}

              {transaction.area && (
                <DataRow>
                  <DataLabel>Living area</DataLabel>
                  <DataValue>{formatArea(transaction.area)}</DataValue>
                </DataRow>
              )}
            </DataGroup>

            {/* Right Column */}
            <DataGroup>
              <DataSectionTitle>Details</DataSectionTitle>

              {transaction.prev_transaction && (
                <DataRow>
                  <DataLabel>Prev. transaction</DataLabel>
                  <DataValue>{transaction.prev_transaction}</DataValue>
                </DataRow>
              )}

              {transaction.num_sellers && (
                <DataRow>
                  <DataLabel>Number of sellers</DataLabel>
                  <DataValue>{transaction.num_sellers}</DataValue>
                </DataRow>
              )}

              {transaction.sellers && (
                <DataRow>
                  <DataLabel>Sellers</DataLabel>
                  <DataValue>{transaction.sellers}</DataValue>
                </DataRow>
              )}

              {transaction.num_buyers && (
                <DataRow>
                  <DataLabel>Number of buyers</DataLabel>
                  <DataValue>{transaction.num_buyers}</DataValue>
                </DataRow>
              )}

              {transaction.buyers && (
                <DataRow>
                  <DataLabel>Buyers</DataLabel>
                  <DataValue>{transaction.buyers}</DataValue>
                </DataRow>
              )}
            </DataGroup>
          </DataGrid>

          {/* Description Section */}
          <DescriptionBox>
            <DataSectionTitle>Description</DataSectionTitle>
            <Description>{transaction.description}</Description>
          </DescriptionBox>

          {/* Property Information */}
          <DataGrid style={{ marginTop: '32px' }}>
            <DataGroup>
              <DataSectionTitle>Information About the Land</DataSectionTitle>

              {transaction.parcel && (
                <DataRow>
                  <DataLabel>Parcel number</DataLabel>
                  <DataValue>{transaction.parcel}</DataValue>
                </DataRow>
              )}

              {transaction.assignment && (
                <DataRow>
                  <DataLabel>Assignment area</DataLabel>
                  <DataValue>{transaction.assignment}</DataValue>
                </DataRow>
              )}
            </DataGroup>
          </DataGrid>

          {/* Properties Section */}
          {transaction.plots && transaction.plots.length > 0 && (
            <DataGroup style={{ marginTop: '32px' }}>
              <DataSectionTitle>Properties Involved</DataSectionTitle>
              <PropertyList>
                {transaction.plots.map((plot, index) => (
                  <PropertyItem key={index}>
                    <div>
                      <PropertyNumber>Parcel {plot.number}</PropertyNumber>
                      <PropertyDescription>
                        {plot.description}
                      </PropertyDescription>
                    </div>
                    <PropertyArea>
                      {typeof plot.area === 'number'
                        ? `${plot.area} m²`
                        : plot.area}
                    </PropertyArea>
                  </PropertyItem>
                ))}
              </PropertyList>
            </DataGroup>
          )}
        </CardBody>
      </Card>
    </DetailContainer>
  )
}
